import React, { useCallback, useContext, useMemo, useState } from "react";
import { BASE_PATH } from "../..";
import { ReactComponent as AddIcon } from '../../assets/icons/add.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { Asset, BatchGroupPoint, Point, SceneDesignerContext } from "../../contexts/SceneDesignerContext";
import "./BatchEditor.scss";
import AssetList from "../layout/AssetList";
import Drawer from 'react-modern-drawer';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import ProductSelector from "./ProductSelector";
import classNames from "classnames";


interface PointProps {
    points: Point[];
    point: BatchGroupPoint;
    onDelete: () => void;
    onChange: (assets: Asset[]) => void;
}

type PointAsset = Point & {
    title: string;
    thumbnail: string;
    name: string;
}

const arraysEqual = (a: string[], b: string[]) => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    for (var i = 0; i < a.length; ++i) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

const BatchEditorPoint: React.FC<PointProps> = ({ point, points, onDelete, onChange }) => {
    const [selectorOpen, setSelectorOpen] = useState(false);

    const productPoint = useMemo(() => {
        const _path = [...point.path];
        const id = _path.pop();
        return points.find(e => e.configuration.id === id && arraysEqual(e.configuration.path, _path));
    }, [point.path, points]);

    const handleSelect = useCallback(() => {
        setSelectorOpen(true);
    }, []);

    const handleCancel = useCallback(() => {
        setSelectorOpen(false);
    }, []);

    const handleSelected = useCallback((assets: Asset[]) => {
        onChange(assets);
        setSelectorOpen(false);
    }, [onChange]);

    if (productPoint === undefined) {
        return null;
    }

    return (
        <>
            <div>
                <div
                    key={productPoint.configuration.id}
                    className={classNames({
                        'batcheditor-item': true,
                    })}>
                    <span className='label'>{productPoint.configuration.id}</span>
                    <span className='name'>{productPoint.configuration.label}</span>
                    <div className={'image multiple'} onClick={handleSelect}>
                            {point.values.map(subitem => <div>
                                {productPoint.type === 'propset' ? <span key={subitem.id}>{subitem.title}</span> : <img key={subitem.id} src={BASE_PATH + subitem.thumbnail} alt='' />}
                            </div>)}
                    </div>
                    <div className='buttons'>
                        <button onClick={handleSelect}><EditIcon /></button>
                        <button onClick={onDelete}><DeleteIcon /></button>
                    </div>
                </div>
                <ProductSelector point={productPoint} open={selectorOpen} onCancel={handleCancel} onProductSelected={handleSelected} selectedProducts={point.values} />
            </div>  
        </>
    )
};

const BatchEditor: React.FC = () => {
    const { configuration, setConfiguration, products } = useContext(SceneDesignerContext);
    const [selectorOpen, setSelectorOpen] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(0);
    const [selectedPoint, setSelectedPoint] = useState<PointAsset[]>([]);

    const pointAssets = products.map(point => ({ ...point, name: [...point.configuration.path, point.configuration.id].join('.'), title: point.configuration.label, thumbnail: point.configuration.value?.thumbnail ?? '' } satisfies PointAsset));

    const handleAdd = useCallback(() => {
        setConfiguration({ ...configuration, batches: [...configuration.batches, { points: [] }] });
    }, [configuration, setConfiguration]);

    const handleAddPoint = useCallback((index: number) => {
        setSelectorOpen(true);
        setSelectedBatch(index);
    }, []);

    const handleCancel = useCallback(() => {
        setSelectorOpen(false);
    }, []);

    const handleDeletePoint = useCallback((batchIndex: number, pointIndex: number) => {
        let batch = configuration.batches[batchIndex];
        if (batch) {
            batch = { ...batch, points: batch.points.filter((e,i) => i !== pointIndex) }
            setConfiguration({ ...configuration, batches: [...configuration.batches.map((e, i) => i === batchIndex ? batch : e)] });
        }
    }, [configuration, setConfiguration]);

    const handleDuplicate = useCallback((batchIndex: number) => {
        let batch = configuration.batches[batchIndex];
        if (batch) {
            const newbatch = { ...batch, points: batch.points.map(e => ({...e})) }
            setConfiguration({ ...configuration, batches: [...configuration.batches, newbatch] });
        }
    }, [configuration, setConfiguration]);

    const handleDeleteGroup = useCallback((batchIndex: number) => {
        setConfiguration({ ...configuration, batches: configuration.batches.filter((e,i) => i !== batchIndex) });
    }, [configuration, setConfiguration]);

    const handleChange = useCallback((batchIndex: number, pointIndex: number, assets: Asset[]) => {
        let batch = configuration.batches[batchIndex];
        if (batch) {
            let point = batch.points[pointIndex];

            if(point){
                point = {...point, values: assets};
                batch = { ...batch, points: [...batch.points.map((e, i) => i === pointIndex ? point : e)] }
                setConfiguration({ ...configuration, batches: [...configuration.batches.map((e, i) => i === batchIndex ? batch : e)] });
            }
        }
    }, [configuration, setConfiguration]);

    const handleSubmit = useCallback(() => {
        let batch = configuration.batches[selectedBatch];
        if (batch) {
            batch = { ...batch, points: [...batch.points, ...selectedPoint.map(point => ({ type: point.type, values: [], path: [...point.configuration.path, point.configuration.id] }))] }
            setConfiguration({ ...configuration, batches: [...configuration.batches.map((e, i) => i === selectedBatch ? batch : e)] });
        }

        setSelectedPoint([]);
        setSelectorOpen(false);
    }, [configuration, selectedBatch, selectedPoint, setConfiguration]);

    return (
        <div className="batcheditor">
            {configuration.batches.map((batch, i) => <div key={i}>
                <h1>Batch {i + 1}</h1>

                <div className="batcheditor-items">
                    {batch.points.map((point, pointIndex) => <BatchEditorPoint 
                        key={point.path.join('.')} 
                        points={products} point={point} 
                        onDelete={() => handleDeletePoint(i, pointIndex)} 
                        onChange={(assets) => handleChange(i, pointIndex, assets)} />
                    )}
                    <div className="batcheditor-item">
                        <div className="image">
                            <button onClick={() => handleAddPoint(i)}><AddIcon /></button>
                        </div>
                    </div>
                    <div>
                        <button onClick={() => handleDeleteGroup(i)} className="primary button">Delete group</button><br />
                        <button onClick={() => handleDuplicate(i)} className="primary button">Duplicate</button>
                    </div>
                </div>

                <hr />

            </div>)}

            <button onClick={handleAdd} className="primary button">Add new group</button>


            <Drawer className='productselector' open={selectorOpen} onClose={handleCancel} direction='right' size={530} overlayOpacity={0} enableOverlay={false}>
                <div className='header'>
                    <h1>Select points</h1>
                    <button className='close' onClick={handleCancel}>
                        <CloseIcon />
                    </button>
                </div>

                <AssetList<PointAsset>
                    assets={pointAssets}
                    onSelectionChanged={setSelectedPoint}
                    selection={selectedPoint}
                    allowMultiSelect={true}
                    className='productselector-items'
                    displayMode='medium' />

                <div className='footer'>
                    <button className='submit' onClick={handleSubmit}>
                        Add
                    </button>
                </div>
            </Drawer>
        </div>
    )
};

export default BatchEditor;